import React, { useState, memo } from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { CheckCircle, Heart } from 'lucide-react';
export function DonatePage() {
  const [isMonthly, setIsMonthly] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-451">
        <div className="text-center mb-12" data-id="element-452">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-453">
            Your Gift Delivers Healthcare to Egypt's Desert Communities
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-454">
            Every donation brings us closer to our March inaugural shipment.
          </p>
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap justify-center gap-6 text-center" data-id="element-455">
          <div className="bg-white px-6 py-3 rounded-lg shadow-sm" data-id="element-456">
            <p className="font-inter text-sm text-primary-deep-blue font-medium" data-id="element-457">
              501(c)(3) Nonprofit
            </p>
          </div>
          <div className="bg-white px-6 py-3 rounded-lg shadow-sm" data-id="element-458">
            <p className="font-inter text-sm text-primary-deep-blue font-medium" data-id="element-459">
              100% Funds Medical Supplies
            </p>
          </div>
          <div className="bg-white px-6 py-3 rounded-lg shadow-sm" data-id="element-460">
            <p className="font-inter text-sm text-primary-deep-blue font-medium" data-id="element-461">
              Secure Payment
            </p>
          </div>
        </div>
      </Section>

      {/* Donation Options */}
      <Section background="white" data-id="element-462">
        <div className="max-w-5xl mx-auto" data-id="element-463">
          {/* Monthly/One-time Toggle */}
          <div className="flex justify-center mb-12" data-id="element-464">
            <div className="inline-flex bg-neutral-offwhite p-1 rounded-lg" data-id="element-465">
              <button className={`px-6 py-3 rounded-md font-inter ${!isMonthly ? 'bg-white shadow-sm' : ''}`} onClick={() => setIsMonthly(false)} data-id="element-466">
                One-time
              </button>
              <button className={`px-6 py-3 rounded-md font-inter ${isMonthly ? 'bg-white shadow-sm' : ''}`} onClick={() => setIsMonthly(true)} data-id="element-467">
                Monthly
              </button>
            </div>
          </div>

          {/* Donation Tiers */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-468">
            {[{
            amount: 25,
            title: 'Basic',
            impact: 'Basic medical supply pack'
          }, {
            amount: 50,
            title: 'Supporter',
            impact: 'Dental supplies for one practitioner',
            highlighted: true
          }, {
            amount: 100,
            title: 'Sustainer',
            impact: 'Full supply kit for clinic station'
          }, {
            amount: 250,
            title: 'Champion',
            impact: 'Shipping costs for one crate'
          }].map((tier, index) => <div key={index} className={`rounded-xl p-6 ${tier.highlighted ? 'border-2 border-primary-gold shadow-lg bg-neutral-offwhite' : 'border border-neutral-offwhite bg-white'}`} data-id="element-469">
                <div className="font-playfair font-bold text-3xl text-primary-deep-blue mb-2" data-id="element-470">
                  ${tier.amount}
                  {isMonthly ? <span className="text-lg" data-id="element-471">/mo</span> : ''}
                </div>
                <h3 className="font-playfair font-bold text-xl text-primary-deep-blue mb-4" data-id="element-472">
                  {tier.title}
                </h3>
                <div className="flex items-start mb-6" data-id="element-473">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-474" />
                  <span className="font-inter text-neutral-charcoal text-sm" data-id="element-475">
                    {tier.impact}
                  </span>
                </div>
                <Button href="https://www.paypal.com/biz/profile/FarashaMedicalAid" variant={tier.highlighted ? 'primary' : 'secondary'} className="w-full" data-id="element-476">
                  Donate {isMonthly ? 'Monthly' : 'Now'}
                </Button>
              </div>)}
          </div>

          {/* Custom Amount */}
          <div className="bg-neutral-offwhite p-8 rounded-xl max-w-2xl mx-auto" data-id="element-477">
            <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-4 text-center" data-id="element-478">
              Custom Amount
            </h3>
            <div className="flex gap-4 mb-4" data-id="element-479">
              <div className="flex-grow" data-id="element-480">
                <input type="number" min="1" placeholder="Enter amount" value={customAmount} onChange={e => setCustomAmount(e.target.value)} className="w-full px-4 py-3 rounded-md border border-neutral-charcoal/20 focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-481" />
              </div>
              <Button href="https://www.paypal.com/biz/profile/FarashaMedicalAid" size="lg" data-id="element-482">
                Donate
              </Button>
            </div>
            <p className="text-sm font-inter text-neutral-charcoal text-center" data-id="element-483">
              Processed securely through PayPal. You don't need a PayPal
              account—credit and debit cards accepted.
            </p>
          </div>
        </div>
      </Section>

      {/* Other Ways to Give */}
      <Section background="sand" data-id="element-484">
        <div className="max-w-4xl mx-auto" data-id="element-485">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-8 text-center" data-id="element-486">
            Other Ways to Give
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-487">
            <div className="bg-white p-6 rounded-xl" data-id="element-488">
              <div className="flex items-center mb-4" data-id="element-489">
                <Heart size={24} className="text-primary-royal-blue mr-3" data-id="element-490" />
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-491">
                  Honor/Memorial Gifts
                </h3>
              </div>
              <p className="font-inter text-neutral-charcoal mb-4" data-id="element-492">
                Make a donation in honor or memory of a loved one. We'll send a
                personalized acknowledgment card.
              </p>
              <Button href="/contact" variant="secondary" data-id="element-493">
                Learn More
              </Button>
            </div>
            <div className="bg-white p-6 rounded-xl" data-id="element-494">
              <div className="flex items-center mb-4" data-id="element-495">
                <CheckCircle size={24} className="text-primary-royal-blue mr-3" data-id="element-496" />
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-497">
                  Employer Matching
                </h3>
              </div>
              <p className="font-inter text-neutral-charcoal mb-4" data-id="element-498">
                Many employers match charitable donations. Check if your company
                participates to double your impact.
              </p>
              <Button href="/contact" variant="secondary" data-id="element-499">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* Tax Info */}
      <Section background="white" data-id="element-500">
        <div className="max-w-3xl mx-auto text-center" data-id="element-501">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-502">
            Your Donation is Tax-Deductible
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-503">
            Farasha Medical Aid is a registered 501(c)(3) nonprofit
            organization. All donations are tax-deductible to the extent allowed
            by law.
          </p>
          <p className="font-inter text-neutral-charcoal" data-id="element-504">EIN: XX-XXXXXXX</p>
        </div>
      </Section>
    </>;
}